import * as Actions from '../actions/conflictActions';
import Conflict from '../../entities/conflict';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ConflictAction = { 
    type: string; 
    conflict: Conflict | null; 
    conflictId: string | Conflict; 
    partial: boolean; 
    lastFetch: number; 
    conflicts: Conflict[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Conflict> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Conflict[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Conflict> | null, firmId: string, userId: string, updates?: Conflict[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'conflict', 'conflicts', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Conflict>} data
 * @param {Conflict[]} [updates]
 * @returns {ArrayMap<Conflict>}
 */
function updateStorage(data: ArrayMap<Conflict>, updates?: Conflict[]): ArrayMap<Conflict> {
	if (logging) console.debug('⮂ conflictReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Conflict[]} [updates]
 * @returns {ArrayMap<Conflict>}
 */
function updateStorageNoReturn(updates?: Conflict[]): void {
	if (logging) console.debug('⮂ conflictReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Conflict[]} [data]
 * @returns {ArrayMap<Conflict>}
 */
function createState(data?: Conflict[] | ArrayMap<Conflict>): ArrayMap<Conflict> {
	if (!data) return ArrayMapRegistry.clone<Conflict>('conflict', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Conflict>('conflict', data.map(e => e));
    else return ArrayMapRegistry.clone<Conflict>('conflict', data);
}

function conflicts(state: ArrayMap<Conflict>, action: ConflictAction): ArrayMap<Conflict> {
	if (!state) state = createState();

	let st: ArrayMap<Conflict>;

	switch (action.type) {
		// action.conflict - the new value of conflict with id action.id
		// action.conflictId - id of the target, used if new value is null
		case Actions.CONFLICT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ conflictReducer: FETCH_ONE_SUCCESS', action);

			if (action.conflict === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.conflictId)) {
				st = state.clone();
				st.set(action.conflict);
				// state.clear();

				updateStorageNoReturn([action.conflict]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.conflicts
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.conflicts
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.conflicts
			 !partial, !stateEmpty, fetchEmpty	-> D return conflicts in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge conflicts in state newer than those in action.conflicts
		 */
		case Actions.CONFLICT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.conflicts.length === 0;
			const fetched = action.conflicts.map(conflict => new Conflict({...conflict, persisted: true}));

			if (logging) console.debug(`⮂ conflictReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} conflicts with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ conflictReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ conflictReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Conflict> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Conflict[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(conflict => {
					const current = st.get(conflict.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && conflict.status === 'D') {
							// Remove it from state
							toUpdate.push(conflict);
							st.delete(conflict);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (conflict.addedOn > current.addedOn) {
								toUpdate.push(conflict);
								st.set(conflict);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (conflict.status !== 'D') {
							toUpdate.push(conflict);
							st.set(conflict);
						}
						else if (logging) console.debug('⮂ conflictReducer: partialMerge: skipping deleted conflict not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ conflictReducer: empty: conflicts', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ conflictReducer: fullMerge: conflicts', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localConflict => {
                    // If this conflict has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localConflict.persisted) {
                        const fetchedConflict = st.get(localConflict.id);
                        if (!fetchedConflict || localConflict.addedOn > fetchedConflict.addedOn) st.set(localConflict);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.CONFLICT_FETCH_ONE_FAILURE:
			console.error('CONFLICT_FETCH_ONE_FAILURE', action.conflictId, action.err);

			// No update to storage required
			return state;

		case Actions.CONFLICT_FETCH_FAILURE:
			console.error('CONFLICT_FETCH_FAILURE', action.conflictId, action.err);

			// No update to storage required
			return state;

		case Actions.CONFLICT_RESTORE: {
		    let conflict = new Conflict({...action.conflict});
            st = state.clone();

            const existing = st.get(conflict.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (conflict.addedOn >= existing.addedOn) {
                    st.set(conflict);
                    return updateStorage(st, [conflict]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (conflict.status === 'D') return state;

                st.set(conflict);
                return updateStorage(st, [conflict]);
            }
        }
            
		case Actions.CONFLICT_SAVE: {
		    // console.debug('⮂ CONFLICT_SAVE reducer', action.conflict?.id);
		    
			// clone the conflict
			let conflict = new Conflict({...action.conflict});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new conflict in the state
			// If this is a create, add to the queue
			const _conflict = st.get(conflict.id);

			if (!_conflict) st.set(conflict);
			else {
				if (conflict.addedOn >= _conflict.addedOn) st.set(conflict);
			}

			return updateStorage(st, [conflict]);
		}

		case Actions.CONFLICT_SAVE_MANY: {
		    if (logging) console.debug('⮂ CONFLICT_SAVE_MANY reducer', action.conflicts.map(conflict => conflict.id).join(','));
		    
			// clone the conflicts
			let toProcess = action.conflicts.map(conflict => new Conflict(conflict));

			st = state.clone();
			// state.clear();

			const updates: Conflict[] = [];

			toProcess.forEach(conflict => {
				// Add or update the new conflict in the state
				// If this is a create, add to the queue
				const existing = st.get(conflict.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (conflict.status !== 'D') {
						st.push(conflict);
						updates.push(conflict);
					}
					else if (logging) console.debug('⮂ CONFLICT_SAVE_MANY: skipping deleted conflict not in state');
				}
				else {
					if (conflict.addedOn >= existing.addedOn) {
						st.set(conflict);
						updates.push(conflict);
					}
					else if (logging) console.debug('⮂ Skipping, existing conflict had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CONFLICT_REMOVE: {
			// clone the conflict
			const conflict = new Conflict({...action.conflict});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(conflict);

			return updateStorage(st, [conflict]);
		}

		case Actions.CONFLICT_RESTORE_FAILURE:
		case Actions.CONFLICT_SAVE_FAILURE: {
		    console.warn('CONFLICT_SAVE_FAILURE reducer', action.conflict?.id);
		    if (!action.conflict?.id) {
				console.warn('CONFLICT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const conflict = ensure(Conflict, action.conflict);
			if (!conflict) {
				console.error('Could not find conflict after call to save');
				return state;
			}

			st = state.clone();

			const _conflictRef = state.get(conflict.id);

			if (_conflictRef && (_conflictRef.addedOn === conflict.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _conflict = new Conflict({..._conflictRef});
				_conflict.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_conflict.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('conflict', _conflict).then(r => {
						if (r.isErr()) console.error(`⮂ conflictReducer: failed to save conflict with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ conflictReducer: saved conflict with fatalError info');
					}).catch(err => console.error(`⮂ conflictReducer: failed to save conflict with fatalError info: ${err.message}`));

				st.set(_conflict);
			}

			return updateStorage(st);
		}

		case Actions.CONFLICT_SAVE_MANY_FAILURE: {
		    console.warn('CONFLICT_SAVE_MANY_FAILURE reducer', action.conflicts.map(conflict => conflict.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.CONFLICT_RESTORE_SUCCESS:
		case Actions.CONFLICT_SAVE_SUCCESS: {
			if (!action.conflict?.id) {
				console.warn('CONFLICT_SAVE_SUCCESS reducer');
                return state;
			}

			const conflictRef = state.get(action.conflict?.id);
			if (!conflictRef) {
				console.warn('Could not find conflict after call to save');
                return state;
			}

			st = state.clone();

			if (action.conflict.addedOn >= conflictRef.addedOn) {
				st.set(action.conflict);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const conflict = new Conflict({...conflictRef, persisted: true});
				st.set(conflict);
			}

			return updateStorage(st, [action.conflict]);
		}

		case Actions.CONFLICT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ CONFLICT_SAVE_MANY_SUCCESS reducer', action.conflicts.map(conflict => conflict.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const conflicts: Conflict[] = [];

			action.conflicts.forEach(e => {
				const conflictRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!conflictRef) console.warn('Could not find conflict after call to save');
				else if (e.addedOn >= conflictRef.addedOn) {
				    st.set(e);
				    conflicts.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const conflict = new Conflict({...conflictRef, persisted: true});
					st.set(conflict);
					conflicts.push(conflict);
				}
			});

			return updateStorage(st, conflicts);
		}

		case Actions.CONFLICT_REMOVE_FAILURE: {
		    console.warn('CONFLICT_REMOVE_FAILURE action', action.conflict?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.CONFLICT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.CONFLICT_RESTORE_MANY: {
			if (logging) console.debug('⮂ CONFLICT_RESTORE_MANY reducer', action.conflicts.map(conflict => conflict.id).join(','));

			const toProcess = action.conflicts.map(conflict => new Conflict({...conflict}));
			st = state.clone();

			const updates: Conflict[] = [];

			toProcess.forEach(conflict => {
				const existing = st.get(conflict.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (conflict.addedOn >= existing.addedOn) {
						st.set(conflict);
						updates.push(conflict);
					}
					else if (logging) console.debug('⮂ CONFLICT_RESTORE_MANY: skipping older version of conflict');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (conflict.status !== 'D') {
						st.set(conflict);
						updates.push(conflict);
					}
					else if (logging) console.debug('⮂ CONFLICT_RESTORE_MANY: skipping deleted conflict not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CONFLICT_REMOVE_MANY: {
			if (logging) console.debug('⮂ CONFLICT_REMOVE_MANY reducer', action.conflicts.map(conflict => conflict.id).join(','));

			const toProcess = action.conflicts.map(conflict => new Conflict({...conflict}));
			st = state.clone();

			toProcess.forEach(conflict => {
				st.delete(conflict);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default conflicts;